import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FileText, Plus, CheckCircle2, FilePlus2, Trash2, ChevronDown,
  ChevronUp, Camera, Euro, Building2, Loader2, Filter, AlertCircle,
  Wrench, ClipboardList, MessageSquare, TrendingUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  supplement: { icon: TrendingUp, color: '#F97316', bg: '#FFF7ED', label: 'typeSupplementLabel' },
  devis:      { icon: FilePlus2,  color: '#3B82F6', bg: '#EFF6FF', label: 'typeDevisLabel' },
  materiel:   { icon: Wrench,     color: '#10B981', bg: '#ECFDF5', label: 'typeMaterielLabel' },
  remarque_client: { icon: MessageSquare, color: '#8B5CF6', bg: '#F5F3FF', label: 'typeRemarqueLabel' },
};

function TypeBadge({ type, t }) {
  const config = TYPE_ICONS[type] || TYPE_ICONS.supplement;
  const Icon = config.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border"
      style={{ background: config.bg, color: config.color, borderColor: `${config.color}30` }}>
      <Icon className="w-3 h-3" />{t(config.label)}
    </span>
  );
}

function StatusBadge({ statut, t }) {
  const styles = {
    a_traiter:    { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', label: 'statusATraiter' },
    traite:       { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'statusTraite' },
    ajoute_devis: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', label: 'statusAjouteDevis' },
  };
  const s = styles[statut] || styles.a_traiter;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${s.bg} ${s.text} ${s.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {t(s.label)}
    </span>
  );
}

// =========== QUICK ADD MODAL ===========
export function AddNoteChantierModal({ open, onClose, chantiers, preselectedChantier = null, onAdded, user }) {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    chantier_id: preselectedChantier?.id || "",
    date: today,
    type: "supplement",
    description: "",
    montant_estime: "",
    photo_data_url: "",
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset when opening
  useEffect(() => {
    if (open) {
      setForm({
        chantier_id: preselectedChantier?.id || "",
        date: today,
        type: "supplement",
        description: "",
        montant_estime: "",
        photo_data_url: "",
      });
      setPhotoPreview(null);
    }
  }, [open, preselectedChantier]);

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, 800, 0.7);
      setPhotoPreview(dataUrl);
      setForm(f => ({ ...f, photo_data_url: dataUrl }));
    } finally { setUploading(false); }
  };

  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.chantier_id || !form.description.trim()) {
      toast.error(t('errorGeneric')); return;
    }
    setSubmitting(true);
    try {
      const chantierNom = chantiers.find(c => c.id === form.chantier_id)?.nom || "";
      await axios.post(`${API}/notes-chantier`, {
        ...form,
        chantier_nom: chantierNom,
        montant_estime: form.montant_estime ? parseFloat(form.montant_estime) : null,
        created_by: `${user?.prenom || ''} ${user?.nom || ''}`.trim(),
      });
      toast.success(t('noteCreated'));
      onAdded?.();
      onClose();
    } catch (e) {
      toast.error(t('errorSaving'));
    } finally { setSubmitting(false); }
  };

  const typeOptions = [
    { value: 'supplement', label: t('typeSupplementLabel'), icon: TrendingUp, color: '#F97316' },
    { value: 'devis',      label: t('typeDevisLabel'),      icon: FilePlus2,  color: '#3B82F6' },
    { value: 'materiel',   label: t('typeMaterielLabel'),   icon: Wrench,     color: '#10B981' },
    { value: 'remarque_client', label: t('typeRemarqueLabel'), icon: MessageSquare, color: '#8B5CF6' },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E2E8F0] text-[#1E293B] max-w-md shadow-xl max-h-[90vh] overflow-y-auto" aria-describedby="note-form-desc">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase font-['Barlow_Condensed'] text-[#1E293B] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#F97316]" />
            {t('addNoteChantier')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Chantier */}
          <div className="space-y-1.5">
            <Label className="text-[#475569] font-medium text-sm">{t('chantiers')} *</Label>
            <Select value={form.chantier_id} onValueChange={v => setForm(f => ({ ...f, chantier_id: v }))}>
              <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] rounded-lg h-11">
                <SelectValue placeholder={t('selectChantier')} />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0] shadow-lg">
                {chantiers.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-[#94A3B8]" />{c.nom}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Type — big visual selector */}
          <div className="space-y-1.5">
            <Label className="text-[#475569] font-medium text-sm">{t('noteType')} *</Label>
            <div className="grid grid-cols-2 gap-2">
              {typeOptions.map(opt => {
                const Icon = opt.icon;
                const selected = form.type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, type: opt.value }))}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left transition-all ${selected ? 'border-current shadow-sm' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}
                    style={selected ? { borderColor: opt.color, background: `${opt.color}10` } : {}}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: selected ? `${opt.color}20` : '#F1F5F9' }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: selected ? opt.color : '#94A3B8' }} />
                    </div>
                    <span className="text-xs font-semibold leading-tight" style={{ color: selected ? opt.color : '#64748B' }}>
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-[#475569] font-medium text-sm">{t('description')} *</Label>
            <Textarea
              required
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Décrivez la note..."
              className="bg-[#F8FAFC] border-[#E2E8F0] rounded-lg min-h-[90px] resize-none text-sm"
            />
          </div>

          {/* Montant + Date row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#475569] font-medium text-sm flex items-center gap-1">
                <Euro className="w-3.5 h-3.5" />{t('montantEstime')}
                <span className="text-[#94A3B8] font-normal text-xs">({t('montantOptional')})</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.montant_estime}
                onChange={e => setForm(f => ({ ...f, montant_estime: e.target.value }))}
                placeholder="0.00"
                className="bg-[#F8FAFC] border-[#E2E8F0] rounded-lg font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#475569] font-medium text-sm">{t('startDate')}</Label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="bg-[#F8FAFC] border-[#E2E8F0] rounded-lg"
              />
            </div>
          </div>

          {/* Photo */}
          <div className="space-y-1.5">
            <Label className="text-[#475569] font-medium text-sm">{t('photoOptional')}</Label>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} />
            {photoPreview ? (
              <div className="relative rounded-xl overflow-hidden h-28">
                <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                <button type="button" onClick={() => { setPhotoPreview(null); setForm(f => ({ ...f, photo_data_url: "" })); }}
                  className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">✕</button>
              </div>
            ) : (
              <button type="button" onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full h-16 border-2 border-dashed border-[#CBD5E1] rounded-xl text-[#94A3B8] text-sm flex items-center justify-center gap-2 hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {uploading ? t('uploading') : t('photoOptional')}
              </button>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#E2E8F0] text-[#475569] rounded-lg">
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#F97316] hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-200"
            >
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><Plus className="w-4 h-4 mr-1" />{t('addNoteChantier')}</>
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =========== MAIN PAGE ===========
export default function NotesChantier() {
  const [notes, setNotes] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("a_traiter");
  const [addOpen, setAddOpen] = useState(false);
  const [expandedChantier, setExpandedChantier] = useState(null);
  const { t } = useLanguage();
  const { user } = useAuth();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [notesRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/notes-chantier`),
        axios.get(`${API}/chantiers`),
      ]);
      setNotes(notesRes.data);
      setChantiers(chantiersRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateStatut = async (id, statut) => {
    try {
      await axios.put(`${API}/notes-chantier/${id}`, { statut });
      const msg = statut === 'traite' ? t('noteMarkedDone') : t('noteAddedToDevis');
      toast.success(msg);
      fetchData();
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const deleteNote = async (id) => {
    try {
      await axios.delete(`${API}/notes-chantier/${id}`);
      toast.success(t('noteDeleted'));
      fetchData();
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  // Filter notes
  const filteredNotes = filter === 'all' ? notes : notes.filter(n => n.statut === filter);

  // Group by chantier
  const grouped = filteredNotes.reduce((acc, note) => {
    const key = note.chantier_id;
    if (!acc[key]) acc[key] = { nom: note.chantier_nom || '?', notes: [] };
    acc[key].notes.push(note);
    return acc;
  }, {});

  // Stats
  const aTraiterCount = notes.filter(n => n.statut === 'a_traiter').length;
  const totalEstime = notes.filter(n => n.statut === 'a_traiter' && n.montant_estime).reduce((s, n) => s + n.montant_estime, 0);

  const filterOptions = [
    { key: 'a_traiter', label: t('filterATraiter'), count: notes.filter(n => n.statut === 'a_traiter').length },
    { key: 'ajoute_devis', label: t('filterDevis'), count: notes.filter(n => n.statut === 'ajoute_devis').length },
    { key: 'traite', label: t('filterTraite'), count: notes.filter(n => n.statut === 'traite').length },
    { key: 'all', label: t('filterAll'), count: notes.length },
  ];

  if (loading) {
    return (
      <div className="space-y-5 animate-fadeIn">
        <div className="skeleton h-8 w-48 mb-2" />
        <div className="skeleton h-24 w-full rounded-2xl" />
        {[1,2,3].map(i => <div key={i} className="skeleton h-32 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div data-testid="notes-chantier-page" className="space-y-5 animate-fadeIn pb-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] uppercase tracking-tight font-['Barlow_Condensed']">
            {t('notesChantierTitle')}
          </h1>
          <p className="text-[#94A3B8] mt-0.5 text-sm">{t('notesChantierSubtitle')}</p>
        </div>
        <Button
          data-testid="btn-add-note-chantier"
          onClick={() => setAddOpen(true)}
          className="bg-[#F97316] hover:bg-orange-600 text-white rounded-lg uppercase tracking-wide text-sm font-medium shadow-md shadow-orange-200 btn-scale"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('addNoteChantier')}
        </Button>
      </div>

      {/* Stats banner */}
      {aTraiterCount > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-[#1E293B]">
                {aTraiterCount} note{aTraiterCount > 1 ? 's' : ''} à traiter
              </p>
              {totalEstime > 0 && (
                <p className="text-sm text-[#64748B]">
                  {t('totalAmount')} : <strong className="text-[#1E293B] font-mono">{totalEstime.toLocaleString('fr-FR')} €</strong>
                </p>
              )}
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setFilter('a_traiter')}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs"
          >
            {t('filterATraiter')}
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {filterOptions.map(opt => (
          <button
            key={opt.key}
            onClick={() => setFilter(opt.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${filter === opt.key ? 'bg-[#1E293B] text-white shadow-sm' : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]'}`}
          >
            {opt.label}
            {opt.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${filter === opt.key ? 'bg-white/20 text-white' : 'bg-[#F1F5F9] text-[#64748B]'}`}>
                {opt.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {Object.keys(grouped).length === 0 ? (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-[#CBD5E1]" />
            <h3 className="text-xl font-semibold text-[#1E293B] mb-2">{t('noNotesToProcess')}</h3>
            <p className="text-[#94A3B8] mb-4">{t('noNotesHintChantier')}</p>
            <Button onClick={() => setAddOpen(true)} className="bg-[#F97316] text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" />{t('addNoteChantier')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([chantierId, group]) => {
            const isExpanded = expandedChantier === null || expandedChantier === chantierId;
            const total = group.notes.filter(n => n.montant_estime).reduce((s, n) => s + n.montant_estime, 0);

            return (
              <Card key={chantierId} className="bg-white border-[#E2E8F0] shadow-sm overflow-hidden">
                {/* Chantier header */}
                <button
                  className="w-full flex items-center justify-between p-4 hover:bg-[#F8FAFC] transition-colors"
                  onClick={() => setExpandedChantier(isExpanded && expandedChantier === chantierId ? null : chantierId)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-[#3B82F6]" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-bold text-[#1E293B]">{group.nom}</h3>
                      <p className="text-xs text-[#94A3B8]">
                        {group.notes.length} note{group.notes.length > 1 ? 's' : ''}
                        {total > 0 && <span className="ml-2 text-[#F97316] font-semibold font-mono">{total.toLocaleString('fr-FR')} €</span>}
                      </p>
                    </div>
                  </div>
                  {isExpanded
                    ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" />
                    : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />
                  }
                </button>

                {/* Notes list */}
                {isExpanded && (
                  <div className="divide-y divide-[#F1F5F9]">
                    {group.notes.map(note => {
                      const typeConf = TYPE_ICONS[note.type] || TYPE_ICONS.supplement;
                      const TypeIcon = typeConf.icon;
                      return (
                        <div key={note.id} data-testid={`note-${note.id}`}
                          className="p-4 hover:bg-[#FAFBFC] transition-colors">
                          <div className="flex items-start gap-3">
                            {/* Type icon */}
                            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                              style={{ background: typeConf.bg }}>
                              <TypeIcon className="w-4 h-4" style={{ color: typeConf.color }} />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <TypeBadge type={note.type} t={t} />
                                <StatusBadge statut={note.statut} t={t} />
                                <span className="text-xs text-[#94A3B8] ml-auto">{note.date}</span>
                              </div>

                              <p className="text-[#1E293B] text-sm mb-2">{note.description}</p>

                              <div className="flex items-center gap-3 flex-wrap">
                                {note.montant_estime && (
                                  <span className="flex items-center gap-1 text-sm font-semibold font-mono text-[#F97316]">
                                    <Euro className="w-3.5 h-3.5" />{note.montant_estime.toLocaleString('fr-FR')} €
                                  </span>
                                )}
                                {note.photo_data_url && (
                                  <span className="text-xs text-[#3B82F6] flex items-center gap-1">
                                    <Camera className="w-3 h-3" /> Photo
                                  </span>
                                )}
                                {note.created_by && (
                                  <span className="text-xs text-[#94A3B8]">{t('by')} {note.created_by}</span>
                                )}
                              </div>

                              {/* Photo thumbnail */}
                              {note.photo_data_url && (
                                <div className="mt-2 w-20 h-20 rounded-lg overflow-hidden border border-[#E2E8F0]">
                                  <img src={note.photo_data_url} alt="note" className="w-full h-full object-cover" />
                                </div>
                              )}

                              {/* Action buttons */}
                              {note.statut === 'a_traiter' && (
                                <div className="flex gap-2 mt-3 flex-wrap">
                                  <Button
                                    size="sm"
                                    onClick={() => updateStatut(note.id, 'ajoute_devis')}
                                    className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg text-xs h-8 btn-scale"
                                  >
                                    <FilePlus2 className="w-3.5 h-3.5 mr-1.5" />
                                    {t('addToDevis')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => updateStatut(note.id, 'traite')}
                                    variant="outline"
                                    className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs h-8 btn-scale"
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    {t('markDone')}
                                  </Button>
                                </div>
                              )}

                              {note.statut === 'ajoute_devis' && (
                                <div className="flex gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    onClick={() => updateStatut(note.id, 'traite')}
                                    variant="outline"
                                    className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs h-8 btn-scale"
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    {t('markDone')}
                                  </Button>
                                </div>
                              )}
                            </div>

                            {/* Delete */}
                            <button
                              onClick={() => deleteNote(note.id)}
                              className="p-1.5 text-[#94A3B8] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 mt-0.5"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add modal */}
      <AddNoteChantierModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        chantiers={chantiers}
        onAdded={fetchData}
        user={user}
      />
    </div>
  );
}
